import { track } from '@ably/ui/core/insights';

// Event identifiers used in triggers in Google Tag Manager.
// Changing these requires an update in GTM.
const GTM_COPY_API_KEY = 'copy-api-key';
const GTM_VIEW_SDK_BUTTON = 'view-sdk-button';
const GTM_FIRST_DASHBOARD_VISIT = 'first-dashboard-visit';
const GTM_FIRST_APP_DASHBOARD_VISIT = 'first-app-dashboard-visit';
const GTM_TRY_IN_BROWSER_BUTTON = 'try-in-browser-button';
const GTM_CLICK_DEV_CONSOLE_TAB = 'click-dev-console-tab';
const GTM_CLEAR_DEV_CONSOLE_EVENTS = 'clear-dev-console-events';
const GTM_CONNECT_DEV_CONSOLE_EVENTS = 'connect-dev-console-events';
const GTM_DISCONNECT_DEV_CONSOLE_EVENTS = 'disconnect-dev-console-events';
const GTM_PAUSE_DEV_CONSOLE_EVENTS = 'pause-dev-console-events';
const GTM_RESUME_DEV_CONSOLE_EVENTS = 'resume-dev-console-events';
const GTM_ATTACH_TO_DEV_CONSOLE_CHANNEL = 'attach-to-dev-console-channel';
const GTM_DETACH_FROM_DEV_CONSOLE_CHANNEL = 'detach-from-dev-console-channel';
const GTM_PUBLISH_MESSAGE_IN_DEV_CONSOLE_CHANNEL = 'publish-message-in-dev-console-channel';
const GTM_ENTER_PRESENCE_IN_DEV_CONSOLE_CHANNEL = 'enter-presence-in-dev-console-channel';
const GTM_LEAVE_PRESENCE_IN_DEV_CONSOLE_CHANNEL = 'leave-presence-in-dev-console-channel';
const GTM_UPDATE_PRESENCE_IN_DEV_CONSOLE_CHANNEL = 'update-presence-in-dev-console-channel';
const GTM_COPY_CODE_BLOCK_CONTENT = 'copy-code-block-content';
const GTM_CLICK_EXPLORE_MAU = 'click-explore-mau';
const GTM_VIEW_CONSUMPTION_OVERVIEW = 'view-consumption-package-overview';
const GTM_CLICK_CONFIRM_AND_PAY = 'click-confirm-pay';
const GTM_SUBMIT_CARD_PAYMENT = 'submit-card-payment';
const GTM_CLICK_SIGNUP_BUTTON = 'click-signup-button';
const GTM_CLICK_USE_CASE_BUTTON = 'click-use-case-button';
const GTM_SIGNUP_VALIDATION_FAILED = 'signup-validation-failed';
const GTM_CLICK_PAID_SIGN_UP_BUTTON = 'click-paid-sign-up-button';
const GTM_CLICK_CONFIRM_AND_PAY_BUTTON = 'click-confirm-and-pay-button';
const GTM_CLICK_CONFIRM_PAYMENT_BUTTON = 'click-confirm-payment-button';
const GTM_FIRST_APP_STATS_PAGE_VISIT = 'first-app-stats-page-visit';
const GTM_GETTING_STARTED_CONFETTI_EVENT = 'getting-started-message';
const GTM_CLICK_GETTING_STARTED_EVENT = 'click-dashboard-getting-started-link';

type DataLayerPayload = { event: string } & Record<string, unknown>;

const push = (data: DataLayerPayload) => {
  const { event, ...rest } = data;
  track(event, rest);
};

const copyApiKeyTracker = ({ apiKeyInPageLocation }: { apiKeyInPageLocation: string }) => {
  push({ event: GTM_COPY_API_KEY, apiKeyInPageLocation });
};

const viewSdkTracker = ({ sdkLanguage }: { sdkLanguage: string }) => {
  push({ event: GTM_VIEW_SDK_BUTTON, sdkLanguage });
};

const firstDashboardVisitTracker = () => {
  push({ event: GTM_FIRST_DASHBOARD_VISIT });
};

const firstAppDashboardVisitTracker = () => {
  push({ event: GTM_FIRST_APP_DASHBOARD_VISIT });
};

const tryInBrowserTracker = () => {
  push({ event: GTM_TRY_IN_BROWSER_BUTTON });
};

const clickSignUpButtonTracker = ({ signupProvider }: { signupProvider: string }) => {
  push({ event: GTM_CLICK_SIGNUP_BUTTON, signupProvider });
};

const clickUseCaseButtonTracker = () => {
  push({ event: GTM_CLICK_USE_CASE_BUTTON });
};

const signUpValidationFailedTracker = () => {
  push({ event: GTM_SIGNUP_VALIDATION_FAILED });
};

const devConsoleTrackers = {
  clickDevConsoleTab: () => {
    push({ event: GTM_CLICK_DEV_CONSOLE_TAB });
  },
  clearDevConsoleEvents: () => {
    push({ event: GTM_CLEAR_DEV_CONSOLE_EVENTS });
  },

  connectDevConsoleEvents: () => {
    push({ event: GTM_CONNECT_DEV_CONSOLE_EVENTS });
  },

  disconnectDevConsoleEvents: () => {
    push({ event: GTM_DISCONNECT_DEV_CONSOLE_EVENTS });
  },

  pauseDevConsoleEvents: () => {
    push({ event: GTM_PAUSE_DEV_CONSOLE_EVENTS });
  },

  resumeDevConsoleEvents: () => {
    push({ event: GTM_RESUME_DEV_CONSOLE_EVENTS });
  },

  attachToDevConsoleChannel: () => {
    push({ event: GTM_ATTACH_TO_DEV_CONSOLE_CHANNEL });
  },

  detachFromDevConsoleChannel: () => {
    push({ event: GTM_DETACH_FROM_DEV_CONSOLE_CHANNEL });
  },

  publishMessageInDevConsoleChannel: () => {
    push({ event: GTM_PUBLISH_MESSAGE_IN_DEV_CONSOLE_CHANNEL });
  },

  enterPresenceInDevConsoleChannel: () => {
    push({ event: GTM_ENTER_PRESENCE_IN_DEV_CONSOLE_CHANNEL });
  },

  leavePresenceInDevConsoleChannel: () => {
    push({ event: GTM_LEAVE_PRESENCE_IN_DEV_CONSOLE_CHANNEL });
  },

  updatePresenceInDevConsoleChannel: () => {
    push({ event: GTM_UPDATE_PRESENCE_IN_DEV_CONSOLE_CHANNEL });
  },
};

const copyCodeBlockContentTracker = ({ copyCodeBlockContent }: { copyCodeBlockContent: string }) => {
  push({ event: GTM_COPY_CODE_BLOCK_CONTENT, copyCodeBlockContent });
};

const clickExploreMauButton = () => {
  push({ event: GTM_CLICK_EXPLORE_MAU });
};

const viewConsumptionPackageOverview = () => {
  push({ event: GTM_VIEW_CONSUMPTION_OVERVIEW });
};

const clickConfirmAndPayButton = () => {
  push({ event: GTM_CLICK_CONFIRM_AND_PAY });
};

const submitCardPayment = () => {
  push({ event: GTM_SUBMIT_CARD_PAYMENT });
};

const clickPaidSignUpButtonTracker = () => {
  push({ event: GTM_CLICK_PAID_SIGN_UP_BUTTON });
};

const clickConfirmAndPayButtonTracker = () => {
  push({ event: GTM_CLICK_CONFIRM_AND_PAY_BUTTON });
};

const clickConfirmPaymentButtonTracker = () => {
  push({ event: GTM_CLICK_CONFIRM_PAYMENT_BUTTON });
};

const firstAppStatsPageVisitEvent = () => {
  push({ event: GTM_FIRST_APP_STATS_PAGE_VISIT });
};

const triggerConfettiMessageEvent = () => {
  push({ event: GTM_GETTING_STARTED_CONFETTI_EVENT });
};

const clickGettingStartedEvent = ({ linkName }: { linkName: string }) => {
  push({ event: GTM_CLICK_GETTING_STARTED_EVENT, linkName });
};

// Fires first-visit events from server-rendered body attributes; shared by both dashboard entrypoints.
const trackFirstVisitEvents = (dataset: DOMStringMap = document.body.dataset) => {
  if (dataset.firstDashboardVisit) firstDashboardVisitTracker();
  if (dataset.firstAppDashboardVisit) firstAppDashboardVisitTracker();
  if (dataset.firstAppStatsPageVisit) firstAppStatsPageVisitEvent();
};

export {
  copyApiKeyTracker,
  viewSdkTracker,
  firstDashboardVisitTracker,
  firstAppDashboardVisitTracker,
  tryInBrowserTracker,
  devConsoleTrackers,
  copyCodeBlockContentTracker,
  clickExploreMauButton,
  viewConsumptionPackageOverview,
  clickConfirmAndPayButton,
  submitCardPayment,
  clickSignUpButtonTracker,
  clickUseCaseButtonTracker,
  signUpValidationFailedTracker,
  clickPaidSignUpButtonTracker,
  clickConfirmAndPayButtonTracker,
  clickConfirmPaymentButtonTracker,
  firstAppStatsPageVisitEvent,
  trackFirstVisitEvents,
  triggerConfettiMessageEvent,
  clickGettingStartedEvent,
};
